import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ShoppingCart, UtensilsCrossed, Plus, Trash2, ArrowLeft, ChevronLeft,
  ChevronRight, ChefHat, BadgePercent, Package, Lock, MapPin,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { FeedGrid, InterestChips } from "../components/pulse/FeedCards";

/* Food & Kitchen — part of My Social Pulse.
   Subsections: Diary (private), Recipes, Deals, Shopping, Pantry.
   Browsing or saving a recipe NEVER creates a meal diary entry. */

const CATS = [["food", "Food"], ["household", "Household"], ["personal", "Personal"], ["other", "Other"]];
const MEALS = [["breakfast", "Breakfast"], ["lunch", "Lunch"], ["dinner", "Dinner"], ["snack", "Snack"]];
const TABS = [
  ["diary", "Diary", UtensilsCrossed],
  ["recipes", "Recipes", ChefHat],
  ["deals", "Deals", BadgePercent],
  ["shopping", "Shopping", ShoppingCart],
];

export default function FoodShopping() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([id]) => id === params.get("tab")) ? params.get("tab") : "diary";
  return (
    <div className="max-w-4xl mx-auto px-5 py-8" data-testid="food-shopping-page">
      <Link to="/my-social-pulse" className="text-sm text-blue-700 font-600 inline-flex items-center gap-1 hover:underline" data-testid="fs-back"><ArrowLeft className="w-4 h-4" /> My Social Pulse</Link>
      <h1 className="font-heading font-900 text-3xl sm:text-4xl tracking-tight mt-2">Food &amp; Kitchen</h1>
      <div className="mt-5 flex gap-2 flex-wrap">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} onClick={() => setParams(id === "diary" ? {} : { tab: id })} data-testid={`fs-tab-${id}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-700 border transition-colors ${tab === id ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-zinc-700 border-zinc-300 hover:border-emerald-600"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
        <Link to="/my-social-pulse/pantry" data-testid="fs-tab-pantry"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-700 border bg-white text-zinc-700 border-zinc-300 hover:border-amber-600 transition-colors">
          <Package className="w-4 h-4" /> Pantry
        </Link>
      </div>
      {tab === "diary" && <FoodDiary />}
      {tab === "recipes" && <RecipesTab />}
      {tab === "deals" && <DealsTab />}
      {tab === "shopping" && <ShoppingList />}
    </div>
  );
}

function RecipesTab() {
  return (
    <div className="mt-5" data-testid="fs-recipes">
      <p className="text-xs text-zinc-500 mb-3">Recipe ideas from FoodPulse. Viewing or saving a recipe never adds anything to your meal diary. "Check ingredients" compares against your pantry and shopping list before adding anything.</p>
      <div className="mb-3"><InterestChips /></div>
      <FeedGrid module="food" type="recipe" emptyText="No recipe suggestions right now." />
    </div>
  );
}

function DealsTab() {
  const [prefs, setPrefs] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => { api.get("/pulse/preferences").then((r) => setPrefs(r.data)).catch(() => setPrefs({})); }, []);

  const save = async (next) => {
    setPrefs(next);
    try { await api.put("/pulse/preferences", next); setReloadKey((k) => k + 1); }
    catch { toast.error("Could not save your deal area"); }
  };

  return (
    <div className="mt-5" data-testid="fs-deals">
      <p className="text-xs text-zinc-500 mb-3">Nearby offers with seller, terms and expiry shown. Availability is always confirmed by the seller when you open an offer — expired deals disappear automatically.</p>
      {prefs && (
        <div className="mb-4 flex flex-wrap items-center gap-2 bg-white border border-zinc-200 rounded-2xl p-3" data-testid="deal-area-picker">
          <MapPin className="w-4 h-4 text-emerald-700 shrink-0" />
          <input value={prefs.area || ""} onChange={(e) => setPrefs((p) => ({ ...p, area: e.target.value }))}
            onBlur={() => save(prefs)} onKeyDown={(e) => e.key === "Enter" && save(prefs)}
            placeholder="Your deal area — e.g. Kochi" data-testid="deal-area-input"
            className="flex-1 min-w-[150px] border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-600" />
          <select value={prefs.radius_km || 25} onChange={(e) => save({ ...prefs, radius_km: Number(e.target.value) })}
            data-testid="deal-radius-select" className="notranslate border border-zinc-300 rounded-lg px-2 py-2 text-sm bg-white">
            {[5, 10, 25, 50, 100].map((k) => <option key={k} value={k}>within {k} km</option>)}
          </select>
          {prefs.area && <button onClick={() => save({ ...prefs, area: "" })} data-testid="deal-area-clear" className="text-xs font-700 text-zinc-500 hover:text-rose-600">Clear</button>}
        </div>
      )}
      <p className="text-[11px] text-zinc-400 mb-3">Your area is chosen manually — never tracked. Offers listing a different place are hidden; the km radius applies where sellers share exact locations.</p>
      <FeedGrid key={reloadKey} module="food" type="offer" emptyText={prefs?.area ? `No active deals near ${prefs.area} right now.` : "No active deals right now."} />
    </div>
  );
}

function ShoppingList() {
  const [items, setItems] = useState(null);
  const [name, setName] = useState("");
  const [qty, setQty] = useState("");
  const [cat, setCat] = useState("food");
  const load = useCallback(() => api.get("/life/shopping").then((r) => setItems(r.data.items)).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/life/shopping", { name: name.trim(), qty, category: cat }); setName(""); setQty(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add item"); }
  };
  const toggle = (it) => api.put(`/life/shopping/${it.id}`, { done: !it.done }).then(load).catch(() => toast.error("Update failed"));
  const del = (it) => api.delete(`/life/shopping/${it.id}`).then(load).catch(() => toast.error("Delete failed"));
  const clearDone = () => api.post("/life/shopping/clear-done").then((r) => { toast.success(`${r.data.removed} cleared`); load(); });

  if (!items) return <p className="mt-6 text-sm text-zinc-500">Loading…</p>;
  const open = items.filter((i) => !i.done), done = items.filter((i) => i.done);
  return (
    <div className="mt-5" data-testid="shopping-list">
      <div className="flex flex-wrap gap-2 bg-white border border-zinc-200 rounded-2xl p-3">
        <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add an item — milk, batteries, soap…" data-testid="shop-name-input"
          className="flex-1 min-w-[180px] border border-zinc-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:border-emerald-600" />
        <input value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Qty" data-testid="shop-qty-input"
          className="w-20 border border-zinc-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:border-emerald-600" />
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="shop-cat-select"
          className="border border-zinc-300 rounded-lg px-2 py-2.5 text-sm bg-white">
          {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button onClick={add} data-testid="shop-add-btn"
          className="inline-flex items-center gap-1.5 bg-emerald-700 text-white px-5 py-2.5 rounded-lg font-700 hover:bg-emerald-600"><Plus className="w-4 h-4" /> Add</button>
      </div>
      <p className="text-xs text-zinc-500 mt-2">Shared with your household — everyone sees and ticks off the same list.</p>

      <ul className="mt-4 space-y-2" data-testid="shop-open-list">
        {open.length === 0 && <li className="text-sm text-zinc-500 py-4 text-center border border-dashed border-zinc-300 rounded-xl">Nothing to buy — add something above.</li>}
        {open.map((it) => (
          <li key={it.id} className="flex items-center gap-3 bg-white border border-zinc-200 rounded-xl px-4 py-3" data-testid={`shop-item-${it.id}`}>
            <input type="checkbox" checked={false} onChange={() => toggle(it)} data-testid={`shop-check-${it.id}`} className="w-5 h-5 accent-emerald-700 cursor-pointer" />
            <span className="text-base font-600">{it.name}</span>
            {it.qty && <span className="text-sm text-zinc-500">× {it.qty}</span>}
            <span className="text-[10px] uppercase font-700 text-zinc-400 border border-zinc-200 rounded px-1.5 py-0.5">{it.category}</span>
            {it.note && <span className="text-xs text-zinc-400 hidden md:inline truncate max-w-[160px]" title={it.note}>{it.note}</span>}
            <span className="ml-auto text-xs text-zinc-400 hidden sm:inline">{it.added_by_name}</span>
            <button onClick={() => del(it)} data-testid={`shop-del-${it.id}`} className="text-zinc-300 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
          </li>
        ))}
      </ul>
      {done.length > 0 && (
        <div className="mt-5">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs font-mono uppercase text-zinc-500">In the basket ({done.length})</span>
            <button onClick={clearDone} data-testid="shop-clear-done" className="text-xs font-700 text-rose-700 hover:underline">Clear</button>
          </div>
          <ul className="space-y-1.5">
            {done.map((it) => (
              <li key={it.id} className="flex items-center gap-3 bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2 opacity-70">
                <input type="checkbox" checked onChange={() => toggle(it)} className="w-5 h-5 accent-emerald-700 cursor-pointer" />
                <span className="text-sm line-through text-zinc-500">{it.name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function FoodDiary() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [items, setItems] = useState(null);
  const [meal, setMeal] = useState("breakfast");
  const [text, setText] = useState("");
  const load = useCallback(() => api.get("/life/food-diary", { params: { date } })
    .then((r) => setItems(r.data.items)).catch(() => setItems([])), [date]);
  useEffect(() => { load(); }, [load]);

  const shift = (d) => setDate(new Date(new Date(date).getTime() + d * 86400000).toISOString().slice(0, 10));
  const add = async () => {
    if (!text.trim()) return;
    try { await api.post("/life/food-diary", { date, meal, text: text.trim() }); setText(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const del = (it) => api.delete(`/life/food-diary/${it.id}`).then(load).catch(() => toast.error("Delete failed"));

  return (
    <div className="mt-5" data-testid="food-diary">
      <p className="text-xs text-zinc-600 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 inline-flex items-center gap-1.5 mb-3"><Lock className="w-3.5 h-3.5" /> Private diary — only you see your entries. Recipes you browse are never logged here.</p>
      <div className="flex items-center gap-2">
        <button onClick={() => shift(-1)} data-testid="fd-prev" className="p-2 border border-zinc-300 rounded-lg hover:border-emerald-600"><ChevronLeft className="w-4 h-4" /></button>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="fd-date"
          className="border border-zinc-300 rounded-lg px-3 py-2 text-sm" />
        <button onClick={() => shift(1)} data-testid="fd-next" className="p-2 border border-zinc-300 rounded-lg hover:border-emerald-600"><ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2 bg-white border border-zinc-200 rounded-2xl p-3">
        <select value={meal} onChange={(e) => setMeal(e.target.value)} data-testid="fd-meal-select"
          className="border border-zinc-300 rounded-lg px-2 py-2.5 text-sm bg-white">
          {MEALS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="What did you eat? e.g. Oatmeal with berries" data-testid="fd-text-input"
          className="flex-1 min-w-[200px] border border-zinc-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:border-emerald-600" />
        <button onClick={add} data-testid="fd-add-btn"
          className="inline-flex items-center gap-1.5 bg-emerald-700 text-white px-5 py-2.5 rounded-lg font-700 hover:bg-emerald-600"><Plus className="w-4 h-4" /> Log</button>
      </div>
      {!items ? <p className="mt-4 text-sm text-zinc-500">Loading…</p> : (
        <div className="mt-4 space-y-4">
          {MEALS.map(([v, l]) => {
            const rows = items.filter((i) => i.meal === v);
            if (rows.length === 0) return null;
            return (
              <div key={v} data-testid={`fd-group-${v}`}>
                <div className="text-xs font-mono uppercase text-emerald-700 font-700 mb-1.5">{l}</div>
                <ul className="space-y-1.5">
                  {rows.map((it) => (
                    <li key={it.id} className="flex items-center gap-3 bg-white border border-zinc-200 rounded-xl px-4 py-2.5">
                      <span className="text-sm">{it.text}</span>
                      <button onClick={() => del(it)} className="ml-auto text-zinc-300 hover:text-rose-600" data-testid={`fd-del-${it.id}`}><Trash2 className="w-4 h-4" /></button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
          {items.length === 0 && <p className="text-sm text-zinc-500 py-4 text-center border border-dashed border-zinc-300 rounded-xl">Nothing logged for this day yet.</p>}
        </div>
      )}
    </div>
  );
}
